/**
 * cart-reservation-timer.js
 *
 * Per-cart-line reservation countdown.
 *
 * Each cart line renders:
 *   <p class="cart-items__reservation"
 *      data-line-key="{{ item.key }}"
 *      data-reservation-duration="600">
 *     Product reserved in cart for <span data-reservation-timer>10:00</span>
 *   </p>
 *
 * This module:
 *  - On first sight of a line, stores its expiry timestamp in localStorage so
 *    the countdown survives refreshes / new sessions / cache.
 *  - Re-uses the stored expiry on subsequent renders (no restart on refresh).
 *  - Ticks the visible timer down once per second.
 *  - On expiry: shows "Reservation expired" for ~2s, then triggers the line's
 *    existing remove button so the theme's cart-items-component handles the
 *    server-side remove + DOM morph + event dispatch (cart count etc. all
 *    update automatically).
 *  - On cart:update (any cart mutation), re-scans the cart DOM and prunes
 *    localStorage entries for lines that are no longer present.
 *
 * Quantity changes do NOT reset the timer — reservation is tied to the line,
 * not the quantity. This matches user-confirmed behavior.
 */

const STORAGE_PREFIX = 'cart-reservation-expiry:';
const TICK_INTERVAL_MS = 1000;
const DEFAULT_DURATION_SECONDS = 600;
const EXPIRED_DISPLAY_MS = 2000;
const EXPIRED_MESSAGE = 'Reservation expired';

let intervalId = null;
/** Track lines that have already shown "expired" so we don't trigger removal twice. */
const expiringLines = new Set();

const storageKey = (lineKey) => STORAGE_PREFIX + lineKey;

function readExpiry(lineKey) {
  try {
    const raw = localStorage.getItem(storageKey(lineKey));
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeExpiry(lineKey, expiryMs) {
  try {
    localStorage.setItem(storageKey(lineKey), String(expiryMs));
  } catch {
    // Quota exceeded or storage disabled — silently ignore.
  }
}

function clearExpiry(lineKey) {
  try {
    localStorage.removeItem(storageKey(lineKey));
  } catch {
    // ignore
  }
}

function formatRemaining(ms) {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return min + ':' + String(sec).padStart(2, '0');
}

function triggerRemove(reservationEl) {
  const row = reservationEl.closest('.cart-items__table-row');
  if (!row) return;
  // The theme renders a per-line button with on:click="/onLineItemRemove/{N}"
  // — clicking it routes through the existing cart-items-component flow which
  // handles the API call, morph, and cart:update event dispatch.
  const removeBtn = row.querySelector('.cart-items__remove');
  if (removeBtn) {
    removeBtn.click();
  }
}

function handleExpiry(reservationEl, lineKey) {
  if (expiringLines.has(lineKey)) return;
  expiringLines.add(lineKey);

  // Capture the line details BEFORE removal so the notice can offer a re-add.
  const removedItem = {
    variantId: reservationEl.dataset.variantId,
    quantity: parseInt(reservationEl.dataset.quantity, 10) || 1,
    title: reservationEl.dataset.productTitle || '',
  };

  // Show the expired state.
  reservationEl.classList.add('is-expired');
  reservationEl.textContent = EXPIRED_MESSAGE;

  // Clear storage immediately so a refresh during the 2s window doesn't
  // restart the timer for this line key.
  clearExpiry(lineKey);

  // Brief pause so the customer sees the message, then trigger remove.
  setTimeout(() => {
    triggerRemove(reservationEl);
    // After the removal has morphed the cart, announce it so the reservation
    // notice can open the drawer and offer to re-add the item.
    if (removedItem.variantId) {
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('cart-reservation:expired', { bubbles: true, detail: removedItem })
        );
      }, 250);
    }
    // Note: after the cart morphs, this element is gone and `expiringLines`
    // still holds the key — that's fine, because the line is also gone. The
    // cleanupStaleEntries pass after cart:update will catch any orphans.
    expiringLines.delete(lineKey);
  }, EXPIRED_DISPLAY_MS);
}

function tick() {
  const elements = document.querySelectorAll('.cart-items__reservation[data-line-key]');
  if (elements.length === 0) {
    stopTicking();
    return;
  }

  const now = Date.now();

  elements.forEach((el) => {
    const lineKey = el.dataset.lineKey;
    if (!lineKey || expiringLines.has(lineKey)) return;

    let expiry = readExpiry(lineKey);

    // First time we've seen this line — initialize and persist its expiry.
    if (expiry == null) {
      const durationSec =
        parseInt(el.dataset.reservationDuration, 10) || DEFAULT_DURATION_SECONDS;
      expiry = now + durationSec * 1000;
      writeExpiry(lineKey, expiry);
    }

    const remaining = expiry - now;

    if (remaining <= 0) {
      handleExpiry(el, lineKey);
      return;
    }

    const timerEl = el.querySelector('[data-reservation-timer]');
    if (timerEl) {
      const next = formatRemaining(remaining);
      // Avoid layout thrash — only write if changed.
      if (timerEl.textContent !== next) {
        timerEl.textContent = next;
      }
    }
  });
}

function startTicking() {
  if (intervalId != null) return;
  intervalId = setInterval(tick, TICK_INTERVAL_MS);
  tick();
}

function stopTicking() {
  if (intervalId == null) return;
  clearInterval(intervalId);
  intervalId = null;
}

/** Drop localStorage entries for line keys no longer present in the cart. */
function cleanupStaleEntries() {
  const liveKeys = new Set();
  document
    .querySelectorAll('.cart-items__reservation[data-line-key]')
    .forEach((el) => liveKeys.add(el.dataset.lineKey));

  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const lineKey = key.slice(STORAGE_PREFIX.length);
        if (!liveKeys.has(lineKey)) toRemove.push(key);
      }
    }
    toRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    });
  } catch {
    // ignore — localStorage might be disabled (private mode etc.)
  }
}

function init() {
  cleanupStaleEntries();
  startTicking();
}

// The cart-items-component dispatches cart:update after every mutation and
// morphs the DOM. By the time we re-scan, the morph is generally complete,
// but a microtask delay smooths over any timing edge cases.
document.addEventListener('cart:update', () => {
  setTimeout(init, 50);
});

// Re-init when restoring from the bfcache (back/forward navigation).
window.addEventListener('pageshow', (event) => {
  if (event.persisted) init();
});

// Initial run — whether the script is parsed during HTML parsing or after.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
